import { z } from 'zod';

export const credentialsSchema = z.object({
  email: z
    .string()
    .min(1, 'Enter your email address')
    .email('Enter a valid email address'),
  // Deliberately no strength rules here. They belong where a password is
  // chosen; applying them at sign-in only tells an attacker what the rules are.
  password: z.string().min(1, 'Enter your password'),
});

export const verificationSchema = z.object({
  code: z
    .string()
    .min(1, 'Enter the code we emailed you')
    .regex(/^\d{4}$/, 'The code is four digits'),
});

export const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'Enter your email address')
    .email('Enter a valid email address'),
});

/** Matches what the backend enforces, so a password is never rejected twice. */
const strongPassword = z
  .string()
  .min(8, 'Use at least 8 characters')
  .regex(/[A-Z]/, 'Include an upper case letter')
  .regex(/[a-z]/, 'Include a lower case letter')
  .regex(/\d/, 'Include a digit');

export const resetPasswordSchema = z
  .object({
    email: z
      .string()
      .min(1, 'Enter your email address')
      .email('Enter a valid email address'),
    code: z
      .string()
      .min(1, 'Enter the code we emailed you')
      .regex(/^\d{4}$/, 'The code is four digits'),
    password: strongPassword,
    confirmPassword: z.string().min(1, 'Type the password again'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'The two passwords do not match',
    path: ['confirmPassword'],
  });

/**
 * Setting a password through an invitation link.
 *
 * The reset form minus the address and the code: the token in the link is
 * what identifies the person, so there is nothing else to ask for. It shares
 * `strongPassword` and the mismatch rule, so an invitation and a reset can
 * never disagree about what a good password is.
 */
export const acceptInvitationSchema = z
  .object({
    password: strongPassword,
    confirmPassword: z.string().min(1, 'Type the password again'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'The two passwords do not match',
    path: ['confirmPassword'],
  });

export type CredentialsValues = z.infer<typeof credentialsSchema>;
export type VerificationValues = z.infer<typeof verificationSchema>;
export type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;
export type AcceptInvitationValues = z.infer<typeof acceptInvitationSchema>;
